import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { AlertOutlineIcon, InlineAlert, Tabs, TabsItem } from '@eventbrite/marmalade';
import type { DestinationEvent } from '@eventbrite/consumer-search.event-renderer';
import { defineMessages, useIntl } from 'react-intl';
import OrganizerInfo from './OrganizerInfo';
import Collections from './Collections';
import { MediaGallery } from './MediaGallery';
import PastEvents from './PastEvents';
import UpcomingEvents from './UpcomingEvents';
import type { Collection } from '@/types/collection';
import type { OrganizerData } from '@/types/organizer';
import { FollowProvider } from './_shared/useFollowContext';
import { FollowButtonGDPRModal } from './_shared/FollowButton/FollowButtonGDPRModal';
import { FollowNotificationBar } from './_shared/FollowButton/FollowNotificationBar';
import {
  EventsViewToggle,
  type EventsDisplayMode,
} from './_shared/EventsViewToggle/EventsViewToggle';
import OrganizerCalendar from './_shared/OrganizerCalendar/OrganizerCalendar';
import type { AuthState } from './_shared/EventsBucket/EventsBucket';
import {
  FeaturedEventPinButton,
  HighlightedEventCard,
  ReplaceFeaturedEventModal,
} from './FeaturedEvent';
import { trackFeaturedEventAction } from '@/utils/featuredEventTracking';
import { useFeaturedEventPinTooltip } from '@/hooks/useFeaturedEventPinTooltip';
import styles from './OrganizerPage.module.scss';

const featuredEventMessages = defineMessages({
  updateError: {
    id: 'We could not update your highlighted event. Please try again.',
    defaultMessage: 'We could not update your highlighted event. Please try again.',
  },
  dismissError: {
    id: 'Dismiss highlighted event error',
    defaultMessage: 'Dismiss highlighted event error',
  },
});

enum OrganizerTab {
  Upcoming = 'Upcoming',
  Past = 'Past',
  Collections = 'Collections',
  Media = 'Media',
}

const MOBILE_EVENTS_LAYOUT_MAX_WIDTH = 660;
const TABLET_MAX_WIDTH = 1023;

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isPastDate(date: Date, today: Date) {
  return startOfDay(date).getTime() < today.getTime();
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function getDateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function matchesMaxWidth(maxWidth: number) {
  if (typeof window === 'undefined') {
    return true;
  }

  if (typeof window.matchMedia === 'function') {
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  }

  return window.innerWidth <= maxWidth;
}

function subscribeToMaxWidth(maxWidth: number, onStoreChange: () => void) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {
      // no-op: matchMedia is not available, nothing to unsubscribe
    };
  }

  const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`);

  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', onStoreChange);
  } else {
    mediaQuery.addListener(onStoreChange);
  }

  window.addEventListener('resize', onStoreChange);

  return () => {
    if (typeof mediaQuery.removeEventListener === 'function') {
      mediaQuery.removeEventListener('change', onStoreChange);
    } else {
      mediaQuery.removeListener(onStoreChange);
    }

    window.removeEventListener('resize', onStoreChange);
  };
}

function useMaxWidthMediaQuery(maxWidth: number) {
  const subscribe = useCallback(
    (onStoreChange: () => void) => subscribeToMaxWidth(maxWidth, onStoreChange),
    [maxWidth],
  );
  const getSnapshot = useCallback(() => matchesMaxWidth(maxWidth), [maxWidth]);
  const getServerSnapshot = useCallback(() => true, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

interface Props {
  serverUrl: string;
  organizer: OrganizerData;
  isOwner: boolean;
  authState: AuthState;
  refreshPhase: 'pending' | 'refreshing' | 'done';
  upcomingEvents: DestinationEvent[];
  featuredEvent?: DestinationEvent | null;
  featuredEventEnabled?: boolean;
  onFeaturedEventChange?: (event: DestinationEvent | null) => Promise<void>;
  isFeaturedEventUpdating?: boolean;
  hasMoreUpcoming: boolean;
  upcomingEventsTotal: number;
  collections: Collection[];
  collectionsHasMore: boolean;
  collectionsTotal: number;
  locale: string;
}

export default function OrganizerPage(props: Props) {
  const intl = useIntl();
  const isMobileEventsLayout = useMaxWidthMediaQuery(MOBILE_EVENTS_LAYOUT_MAX_WIDTH);
  const isTabletOrBelow = useMaxWidthMediaQuery(TABLET_MAX_WIDTH);
  const isDesktop = !isTabletOrBelow;
  const today = useMemo(() => startOfDay(new Date()), []);
  const yesterday = useMemo(() => addDays(today, -1), [today]);
  const [activeTab, setActiveTab] = useState<OrganizerTab>(OrganizerTab.Upcoming);
  const [eventsDisplayMode, setEventsDisplayMode] = useState<EventsDisplayMode>('grid');
  const [calendarSelectedDate, setCalendarSelectedDate] = useState<Date>(today);
  const [calendarDateFilter, setCalendarDateFilter] = useState<{
    dateKey: string;
    tab: OrganizerTab.Upcoming | OrganizerTab.Past;
    isExplicit: boolean;
  } | null>(null);
  const [pastEventsAvailability, setPastEventsAvailability] = useState<{
    isReady: boolean;
    hasEvents: boolean;
  }>({ isReady: false, hasEvents: false });
  const [replacementCandidate, setReplacementCandidate] = useState<DestinationEvent | null>(null);
  const [mutationError, setMutationError] = useState(false);
  const { shouldShowTooltip: shouldShowPinTooltip, markPinned } = useFeaturedEventPinTooltip(
    props.featuredEventEnabled === true && props.isOwner,
  );

  const handleFeaturedEventAction = useCallback(
    async (event: DestinationEvent) => {
      setMutationError(false);
      trackFeaturedEventAction('pin_control_clicked', props.organizer.id, event.id);

      if (props.featuredEvent?.id === event.id) {
        try {
          await props.onFeaturedEventChange?.(null);
          trackFeaturedEventAction('highlight_cleared', props.organizer.id, event.id);
        } catch {
          setMutationError(true);
        }
        return;
      }

      if (props.featuredEvent) {
        setReplacementCandidate(event);
        return;
      }

      try {
        await props.onFeaturedEventChange?.(event);
        markPinned({ organizerId: props.organizer.id, eventId: event.id });
      } catch {
        setMutationError(true);
      }
    },
    [markPinned, props.featuredEvent, props.onFeaturedEventChange, props.organizer.id],
  );

  const confirmReplacement = useCallback(() => {
    if (!replacementCandidate) return;
    const candidate = replacementCandidate;
    // Dismiss right away: the update is optimistic, so the card already reflects the swap.
    setMutationError(false);
    setReplacementCandidate(null);
    trackFeaturedEventAction('replacement_confirmed', props.organizer.id, candidate.id);

    const update = props.onFeaturedEventChange;
    if (!update) return;
    void update(candidate)
      .then(() => {
        markPinned({ organizerId: props.organizer.id, eventId: candidate.id });
      })
      .catch(() => {
        setMutationError(true);
      });
  }, [markPinned, props.onFeaturedEventChange, props.organizer.id, replacementCandidate]);

  const renderFeaturedEventAction = useCallback(
    (event: DestinationEvent) => (
      <FeaturedEventPinButton
        eventName={event.name}
        isPinned={props.featuredEvent?.id === event.id}
        showTooltip={shouldShowPinTooltip}
        disabled={props.isFeaturedEventUpdating}
        onClick={() => void handleFeaturedEventAction(event)}
      />
    ),
    [
      handleFeaturedEventAction,
      props.featuredEvent?.id,
      props.isFeaturedEventUpdating,
      shouldShowPinTooltip,
    ],
  );

  const setDefaultCalendarDateForTab = useCallback(
    (tab: OrganizerTab) => {
      if (tab === OrganizerTab.Upcoming) {
        setCalendarSelectedDate(today);
        setCalendarDateFilter({
          dateKey: getDateKey(today),
          tab: OrganizerTab.Upcoming,
          isExplicit: false,
        });
        return;
      }

      if (tab === OrganizerTab.Past) {
        setCalendarSelectedDate(yesterday);
        setCalendarDateFilter({
          dateKey: getDateKey(yesterday),
          tab: OrganizerTab.Past,
          isExplicit: false,
        });
      }
    },
    [today, yesterday],
  );

  const handleCalendarDateSelect = (date: Date) => {
    const nextTab = isPastDate(date, today) ? OrganizerTab.Past : OrganizerTab.Upcoming;

    setCalendarSelectedDate(date);
    setCalendarDateFilter({
      dateKey: getDateKey(date),
      tab: nextTab,
      isExplicit: true,
    });
    setActiveTab(nextTab);
  };

  const isDateFilteredEventsMode = isMobileEventsLayout || eventsDisplayMode === 'list';

  const handleTabChange = (tab: OrganizerTab) => {
    if (tab === activeTab) {
      return;
    }

    setActiveTab(tab);

    if (tab === OrganizerTab.Upcoming || tab === OrganizerTab.Past) {
      if (isDateFilteredEventsMode) {
        setDefaultCalendarDateForTab(tab);
      } else {
        setCalendarDateFilter(null);
      }
    }
  };

  const handleEventsDisplayModeChange = (mode: EventsDisplayMode) => {
    setEventsDisplayMode(mode);

    if (mode === 'grid') {
      setCalendarDateFilter(null);
      return;
    }

    setDefaultCalendarDateForTab(activeTab);
  };

  const showUpcomingToggle =
    activeTab === OrganizerTab.Upcoming && (isMobileEventsLayout || props.upcomingEventsTotal > 0);
  const showPastToggle =
    activeTab === OrganizerTab.Past &&
    pastEventsAvailability.isReady &&
    pastEventsAvailability.hasEvents;
  const showCalendarControls = showUpcomingToggle || showPastToggle;
  const showEventsViewToggle = !isMobileEventsLayout && (showUpcomingToggle || showPastToggle);
  const effectiveEventsDisplayMode: EventsDisplayMode = isMobileEventsLayout
    ? 'grid'
    : eventsDisplayMode;
  const showCalendar = isDateFilteredEventsMode && showCalendarControls;
  const activeUpcomingDateFilter =
    calendarDateFilter?.tab === OrganizerTab.Upcoming ? calendarDateFilter.dateKey : null;
  const activePastDateFilter =
    calendarDateFilter?.tab === OrganizerTab.Past ? calendarDateFilter.dateKey : null;
  const upcomingEventsDateFilter = isDateFilteredEventsMode
    ? (activeUpcomingDateFilter ?? getDateKey(today))
    : null;
  const pastEventsDateFilter = isDateFilteredEventsMode
    ? (activePastDateFilter ?? getDateKey(yesterday))
    : null;

  const mobileCalendarControl = isMobileEventsLayout ? (
    <OrganizerCalendar
      locale={props.locale}
      organizerId={props.organizer.id}
      selectedDate={calendarSelectedDate}
      onSelectDate={handleCalendarDateSelect}
    />
  ) : undefined;

  useEffect(() => {
    if (!isDateFilteredEventsMode) {
      return;
    }

    const hasSelectionForActiveTab = calendarDateFilter?.tab === activeTab;
    if (hasSelectionForActiveTab) {
      return;
    }

    setDefaultCalendarDateForTab(activeTab);
  }, [activeTab, calendarDateFilter, isDateFilteredEventsMode, setDefaultCalendarDateForTab]);

  return (
    <FollowProvider organizerId={props.organizer.id} organizerName={props.organizer.name}>
      <FollowNotificationBar />
      <FollowButtonGDPRModal />
      <div className={styles.root}>
        <OrganizerInfo
          data={props.organizer}
          isOwner={props.isOwner}
          refreshPhase={props.refreshPhase}
          eventbriteBaseUrl={props.serverUrl}
        />
        {props.featuredEventEnabled && props.featuredEvent ? (
          <HighlightedEventCard
            event={props.featuredEvent}
            locale={props.locale}
            organizerId={props.organizer.id}
            authState={props.authState}
            action={props.isOwner ? renderFeaturedEventAction(props.featuredEvent) : undefined}
          />
        ) : null}
        <div className={styles.container}>
          {props.featuredEventEnabled && mutationError ? (
            <InlineAlert
              variant="danger"
              icon={<AlertOutlineIcon aria-hidden />}
              description={intl.formatMessage(featuredEventMessages.updateError)}
              closeOnClick={() => setMutationError(false)}
              closeTitle={intl.formatMessage(featuredEventMessages.dismissError)}
              className={styles.featuredEventError}
            />
          ) : null}
          <div className={styles.eventsToolbar}>
            <div className={styles.tabsRegion}>
              <Tabs
                value={activeTab}
                onValueChange={(tab) => handleTabChange(tab as OrganizerTab)}
                aria-label={intl.formatMessage({
                  id: 'Organizer event tabs',
                  defaultMessage: 'Organizer event tabs',
                })}
                variant="primary"
              >
                <TabsItem key={OrganizerTab.Upcoming} value={OrganizerTab.Upcoming}>
                  {intl.formatMessage({ id: 'Upcoming', defaultMessage: 'Upcoming' })}
                </TabsItem>
                <TabsItem key={OrganizerTab.Past} value={OrganizerTab.Past}>
                  {intl.formatMessage({ id: 'Past', defaultMessage: 'Past' })}
                </TabsItem>
                {props.collections.length > 0 && (
                  <TabsItem key={OrganizerTab.Collections} value={OrganizerTab.Collections}>
                    {intl.formatMessage({ id: 'Collections', defaultMessage: 'Collections' })}
                  </TabsItem>
                )}
                {props.organizer.medias.length > 0 && (
                  <TabsItem key={OrganizerTab.Media} value={OrganizerTab.Media}>
                    {intl.formatMessage({ id: 'Media', defaultMessage: 'Media' })}
                  </TabsItem>
                )}
              </Tabs>
            </div>
            {showEventsViewToggle ? (
              <div className={styles.viewToggleRegion}>
                <EventsViewToggle
                  mode={eventsDisplayMode}
                  onModeChange={handleEventsDisplayModeChange}
                />
              </div>
            ) : null}
            {showCalendar && !isDesktop && !isMobileEventsLayout ? (
              <div className={styles.calendarToggleRegion}>
                <OrganizerCalendar
                  locale={props.locale}
                  organizerId={props.organizer.id}
                  selectedDate={calendarSelectedDate}
                  onSelectDate={handleCalendarDateSelect}
                />
              </div>
            ) : null}
          </div>
          <div
            className={`${styles.content} ${showCalendar && isDesktop ? styles.contentWithCalendar : ''}`}
          >
            <div className={styles.primaryContent}>
              {activeTab === OrganizerTab.Upcoming && (
                <UpcomingEvents
                  organizerId={props.organizer.id}
                  eventbriteBaseUrl={props.serverUrl}
                  initialEvents={props.upcomingEvents}
                  initialHasMore={props.hasMoreUpcoming}
                  initialTotal={props.upcomingEventsTotal}
                  locale={props.locale}
                  eventsDisplayMode={effectiveEventsDisplayMode}
                  selectedDateFilter={upcomingEventsDateFilter}
                  isExplicitDateFilter={
                    calendarDateFilter?.tab === OrganizerTab.Upcoming &&
                    calendarDateFilter.isExplicit
                  }
                  includeStarted={upcomingEventsDateFilter === getDateKey(today)}
                  mobileCalendarControl={mobileCalendarControl}
                  authState={props.authState}
                  highlightedEventId={
                    props.featuredEventEnabled ? props.featuredEvent?.id : undefined
                  }
                  renderFeaturedEventAction={
                    props.featuredEventEnabled && props.isOwner
                      ? renderFeaturedEventAction
                      : undefined
                  }
                />
              )}
              {activeTab === OrganizerTab.Past && (
                <PastEvents
                  organizerId={props.organizer.id}
                  eventbriteBaseUrl={props.serverUrl}
                  locale={props.locale}
                  eventsDisplayMode={effectiveEventsDisplayMode}
                  onPastEventsAvailabilityChange={setPastEventsAvailability}
                  selectedDateFilter={pastEventsDateFilter}
                  mobileCalendarControl={mobileCalendarControl}
                  authState={props.authState}
                />
              )}
              {activeTab === OrganizerTab.Collections && (
                <Collections
                  organizerId={props.organizer.id}
                  initialCollections={props.collections}
                  initialHasMore={props.collectionsHasMore}
                  initialTotal={props.collectionsTotal}
                />
              )}
              {activeTab === OrganizerTab.Media && <MediaGallery medias={props.organizer.medias} />}
            </div>
            {showCalendar && isDesktop ? (
              <aside className={styles.calendarSidebar}>
                <OrganizerCalendar
                  locale={props.locale}
                  organizerId={props.organizer.id}
                  selectedDate={calendarSelectedDate}
                  onSelectDate={handleCalendarDateSelect}
                />
              </aside>
            ) : null}
          </div>
        </div>
      </div>
      {props.featuredEventEnabled ? (
        <ReplaceFeaturedEventModal
          candidate={replacementCandidate}
          isPending={props.isFeaturedEventUpdating ?? false}
          onClose={() => {
            if (!props.isFeaturedEventUpdating) {
              setReplacementCandidate(null);
            }
          }}
          onConfirm={confirmReplacement}
        />
      ) : null}
    </FollowProvider>
  );
}
